import React, { FC, useRef } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';
import { defineMessages, useIntl } from 'react-intl';
import IconButton from '../core/IconButton';
import { cn } from '../../helpers/classnames';
import { IconMapPin, IconDragHandle } from '../../icons';

const messages = defineMessages({
  reorder: {
    id: 'check_in_plan_management.reorder_stop',
    defaultMessage: 'Reorder stop',
  },
});

export interface CheckInDnDItemProps {
  id: string;
  label: string;
  subText?: string;
  hoverIndex: number;
  onMove: (id: string, dragIndex: number, hoverIndex: number) => void;
}

export interface DnDItem {
  index: number;
  id: string;
  type: string;
}

const CheckInDnDItem: FC<CheckInDnDItemProps> = ({
  id,
  label,
  hoverIndex,
  subText,
  onMove,
}) => {
  const intl = useIntl();
  const ref = useRef<HTMLLIElement>(null);
  const [{ handlerId }, drop] = useDrop({
    accept: 'stop',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DnDItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Check that the mouse has crossed half of the item height in up or down direction
      if (
        (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
        (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)
      ) {
        return;
      }

      // Time to actually perform the action
      onMove(id, dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'stop',
    item: () => {
      return { id, hoverIndex };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));
  return (
    <li
      ref={ref}
      key={id}
      data-handler-id={handlerId}
      className="relative flex items-center justify-start px-4 py-1"
    >
      <div
        className={cn(
          'flex w-full cursor-move flex-row items-center gap-3',
          isDragging && 'opacity-50',
        )}
      >
        <IconMapPin className="size-6 text-yellow-500" />
        <span className="text-base font-semibold leading-6">{label}</span>
        <span className="text-base leading-6">{subText}</span>
        <div className="flex grow justify-end">
          <IconButton
            size="sm"
            variant="ghost"
            icon={<IconDragHandle />}
            aria-label={intl.formatMessage(messages.reorder)}
          />
        </div>
      </div>
    </li>
  );
};

export default CheckInDnDItem;
